import React from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { CalendarIcon, SlidersHorizontal, X } from "lucide-react";
import { format, parseISO, isValid } from "date-fns";
import { formatDate } from "@/lib/dateFormat";

// Phase 97 Batch 2 — Advanced Filtering Engine, shared across Revenue +
// Expenses. A compact popover date-picker for filter bars (distinct from
// EditableDate — this is a standalone control, not tied to a row).
export function FilterDate({ value, onChange, placeholder = "Any date", testId }) {
  const [open, setOpen] = React.useState(false);
  const selected = value && isValid(parseISO(value)) ? parseISO(value) : undefined;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={`h-8 text-xs rounded-sm border border-slate-200 bg-white px-2 inline-flex items-center gap-1 whitespace-nowrap hover:border-slate-300 ${!selected ? "text-slate-400" : "text-slate-700"}`}
          data-testid={testId}
        >
          <CalendarIcon size={11} className="shrink-0 text-slate-400" />
          {selected ? formatDate(selected) : placeholder}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="start">
        <Calendar mode="single" selected={selected} onSelect={(d) => { if (d) { onChange(format(d, "yyyy-MM-dd")); setOpen(false); } }} initialFocus />
        {selected && (
          <div className="border-t border-slate-100 p-1.5">
            <button type="button" onClick={() => { onChange(""); setOpen(false); }} className="w-full text-center text-xs text-slate-500 hover:text-red-600 py-1 rounded-sm hover:bg-slate-50">
              Clear
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

// Small labeled wrapper so every filter control looks consistent (label
// above the control, matching the ledger's overall spreadsheet aesthetic).
export function FilterField({ label, children, testId }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="text-[10px] uppercase tracking-wide text-slate-400 font-medium">{label}</span>
      {children}
    </div>
  );
}

// Collapsible filter bar container — orange accent to match the studio's
// bulk-action styling. `count` = number of active filters.
export function FilterBar({ open, onToggle, count, onClear, children, testId }) {
  return (
    <div className="mb-3" data-testid={testId}>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onToggle}
          className={`inline-flex items-center gap-1.5 h-8 px-3 rounded-sm text-xs font-medium border transition-colors ${count > 0 ? "border-[#F54800] text-[#F54800] bg-orange-50" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}
          data-testid={testId ? `${testId}-toggle` : undefined}
        >
          <SlidersHorizontal size={12} /> Filters {count > 0 ? `(${count})` : ""}
        </button>
        {count > 0 && (
          <button type="button" onClick={onClear} className="inline-flex items-center gap-1 h-8 px-2 rounded-sm text-xs text-slate-500 hover:text-red-600" data-testid={testId ? `${testId}-clear-all` : undefined}>
            <X size={12} /> Clear all
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 p-3 border border-slate-200 rounded-sm bg-slate-50/60 flex flex-wrap gap-3 items-end">
          {children}
        </div>
      )}
    </div>
  );
}
